import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentMethods } from '../contexts/PaymentMethodsContext';
import { CreditCard, ChevronLeft, Trash2, MoreHorizontal, Loader2 } from 'lucide-react';
import { ListSection } from '../components/ios';

function PaymentMethodsView() {
  const navigate = useNavigate();
  const {
    paymentMethods,
    loading,
    error,
    loadPaymentMethods,
    deletePaymentMethod
  } = usePaymentMethods();

  const [localError, setLocalError] = useState('');
  const [menuFor, setMenuFor] = useState(null); // payment method id with open context menu

  // Load payment methods when component mounts
  useEffect(() => {
    loadPaymentMethods();
  }, []); // Empty dependency array to prevent infinite loop

  const handleDelete = async (method) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      setLocalError('');
      await deletePaymentMethod(method.id);
    } catch (err) {
      setLocalError(err.message || 'Failed to delete payment method');
    }
  };

  const cardDisplayName = (method) => `Card •••• ${method.last4 || ''}`;

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: back chevron + centered title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="flex items-center -ml-2 text-[#FF3B30] active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            Payment Methods
          </h1>
          <span className="w-6" />
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        {/* Error banner */}
        {displayError && (
          <div className="px-4 mt-4">
            <div className="bg-white rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <p className="text-[15px] text-[#FF3B30]">{displayError}</p>
            </div>
          </div>
        )}

        {loading ? (
          <ListSection>
            <div className="px-4 py-6 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-[#8E8E93]" />
            </div>
          </ListSection>
        ) : paymentMethods.length > 0 ? (
          /* Saved Cards */
          <ListSection title="Saved Cards">
            {paymentMethods.map((method) => (
              <div key={method.id} className="relative">
                <div className="flex items-center gap-3 px-4 py-3 min-h-[44px]">
                  <span className="w-[30px] h-[30px] rounded-[7px] bg-[#007AFF] flex items-center justify-center shrink-0">
                    <CreditCard size={17} className="text-white" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[16px] font-semibold text-gray-900 truncate">
                      {cardDisplayName(method)}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setMenuFor(menuFor === method.id ? null : method.id)}
                    className="p-1 shrink-0 text-[#8E8E93] active:opacity-60"
                    aria-label="Card options"
                  >
                    <MoreHorizontal size={20} />
                  </button>
                </div>

                {menuFor === method.id && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setMenuFor(null)} />
                    <div className="absolute right-3 top-10 z-40 w-48 bg-white rounded-xl shadow-xl border border-black/[0.06] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => {
                          setMenuFor(null);
                          handleDelete(method);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-[15px] text-[#FF3B30] active:bg-gray-100"
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </ListSection>
        ) : (
          /* Empty state — mirrors iOS creditcard.circle + message */
          <ListSection>
            <div className="px-4 py-8 flex flex-col items-center text-center gap-2">
              <CreditCard size={50} className="text-[#8E8E93]" />
              <p className="text-[17px] font-semibold text-[#6D6D72]">No Payment Methods</p>
              <p className="text-[15px] text-[#6D6D72]">
                Save a card at checkout to make ordering faster
              </p>
            </div>
          </ListSection>
        )}
      </div>
    </div>
  );
}

export default PaymentMethodsView;
